import {
  useGetValidationTasksApiV1ModelValidationTasksGet,
  useValidationTaskActionsEndpointApiV1ModelValidationTasksTaskIdPatch,
} from "../../../../vl-api/vlComponents";
import { App } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateActiveQueryByOperationId } from "../../../../vl-api/vlUtils";
import last from "lodash/last";
import { ValidationTaskResponse } from "../../../../vl-api/vlSchemas";
import { CheckCircleOutlined } from "@ant-design/icons";
import styles from "./TrainModel.module.scss";

const POLLING_INTERVAL_MS = 2000;

export const useValidationTasks = (datasetId: string) => {
  const { notification, modal } = App.useApp();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } =
    useGetValidationTasksApiV1ModelValidationTasksGet<{
      tasks: ValidationTaskResponse[];
    }>(
      {
        queryParams: { dataset_id: datasetId },
      },
      {
        refetchInterval: ({ state }) => {
          const lastTask = last(state.data?.tasks);
          if (lastTask?.status === "IN_PROGRESS") {
            return POLLING_INTERVAL_MS;
          }
        },
      },
    );

  const validationActionsMutation =
    useValidationTaskActionsEndpointApiV1ModelValidationTasksTaskIdPatch({
      onSuccess: () => {
        notification.success({ message: "Validation canceled" });
        invalidateActiveQueryByOperationId(
          queryClient,
          "getValidationTasksApiV1ModelValidationTasksGet",
        );
      },
    });

  const handleCancelTaskClick = (taskId: string) => {
    modal.confirm({
      title: "Cancel validation",
      content: "Are you sure you want to cancel validation?",
      okText: "Yes",
      onOk: () =>
        validationActionsMutation.mutate({
          pathParams: { taskId },
          queryParams: { action: "CANCEL" },
        }),
      cancelText: "No",
    });
  };

  const handleCompletedClick = (onViewModelClick: () => void) => {
    modal.confirm({
      title: <div className={styles.successColor}>Validation Completed</div>,
      content:
        "Your model validation has completed successfully. You can explore the results in the Model Catalog.",
      okText: "View Model",
      onOk: onViewModelClick,
      icon: <CheckCircleOutlined className={styles.successColor} />,
      closable: true,
      cancelButtonProps: { style: { display: "none" } },
    });
  };

  return {
    handleCancelTaskClick,
    handleCompletedClick,
    task: last<ValidationTaskResponse>(data?.tasks),
    isLoading,
    isError,
  };
};
